import { useEffect, useMemo, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, AreaChart, Area, Cell,
} from "recharts";
import { AlertTriangle, Radio, Database, Circle } from "lucide-react";
import { getDashboard, listChannels, listCampaigns } from "../lib/api";
import RoutingPanel from "../components/RoutingPanel";

const Panel = ({ title, children, testid, note }) => (
    <div data-testid={testid} className="bg-[#0a0a0a] border border-[#262626] p-5">
        <div className="flex items-center justify-between mb-4">
            <div className="d8-label">{title}</div>
            {note && <div className="d8-label text-[#ffb800]">{note}</div>}
        </div>
        {children}
    </div>
);

const chartTooltipStyle = {
    backgroundColor: "#050505",
    border: "1px solid #262626",
    color: "#f5f5f0",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 12,
    borderRadius: 0,
};

const STATE_META = {
    live: { label: "LIVE", cls: "text-[#f5f5f0] border-[#f5f5f0] bg-[#f5f5f010]", Icon: Radio },
    mixed: { label: "MIXED · LIVE + DEMO", cls: "text-[#ffb800] border-[#ffb800] bg-[#ffb80012]", Icon: AlertTriangle },
    demo: { label: "DEMO / MOCK DATA", cls: "text-[#ffb800] border-[#ffb800] bg-[#ffb80012]", Icon: Database },
    empty: { label: "EMPTY", cls: "text-[#8a8a8a] border-[#262626] bg-[#121212]", Icon: Circle },
};

const THEMES = ["all", "workplace", "dating", "economy", "ai", "housing", "healthcare", "loneliness", "social_media", "institutional_trust", "existential_absurdity"];
const TIERS = ["all", "free_public", "edgy_public", "premium_private", "internal_test"];

export const Dashboard = () => {
    const [filters, setFilters] = useState({
        platform: "all",
        channel_id: "all",
        campaign_id: "all",
        theme: "all",
        darkness_level: "all",
        content_tier: "all",
        include_demo: true,
    });
    const [channels, setChannels] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        listChannels().then(setChannels).catch(() => {});
        listCampaigns().then(setCampaigns).catch(() => {});
    }, []);

    const load = async () => {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => {
            if (v === "all" || v === "" || v === null) return;
            params[k] = v;
        });
        try {
            setData(await getDashboard(params));
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const stateMeta = useMemo(
        () => (data ? STATE_META[data.data_state] || STATE_META.empty : STATE_META.empty),
        [data]
    );

    if (err) {
        return <div data-testid="dashboard-error" className="p-12 text-[#ff2a2a]">Failed: {err}</div>;
    }
    if (!data) {
        return <div data-testid="dashboard-loading" className="p-12 d8-label">Loading signal…</div>;
    }

    const showLive = data.data_state === "live" || data.data_state === "mixed";
    const showDemo = data.data_state === "demo" || data.data_state === "mixed";
    const isEmpty = data.data_state === "empty";

    // Pick primary dataset for charts
    const primary = showLive ? data.live : data.demo || { retention_curve: [], platform_breakdown: [], theme_breakdown: [], darkness_distribution: [], sentiment_clusters: [] };

    return (
        <div data-testid="dashboard-page" className="min-h-screen">
            <section className="border-b border-[#262626] px-6 md:px-12 py-10 noise-bg scanline relative overflow-hidden">
                <div className="d8-label mb-3">/ Signal Dashboard</div>
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                    Aggregate cultural signal.
                </h1>
                <div
                    data-testid="state-badge"
                    data-state={data.data_state}
                    className={`mt-6 inline-flex items-center gap-2 border font-mono-d8 text-[11px] uppercase tracking-widest px-3 py-2 ${stateMeta.cls}`}
                >
                    <stateMeta.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                    {stateMeta.label}
                </div>
                <div data-testid="mock-banner" className="mt-3 text-xs text-[#8a8a8a]">
                    Aggregate only · no individual profiling · AI may suggest strategy but does not fabricate performance.
                </div>
            </section>

            {/* Filters */}
            <section className="px-6 md:px-12 py-6 border-b border-[#262626]">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <FilterSelect testid="filter-platform" label="Platform" value={filters.platform} onChange={(v) => setFilters((f) => ({ ...f, platform: v }))}
                        options={["all", "tiktok", "reels", "shorts", "instagram", "facebook", "youtube_shorts", "x", "ifunny", "other"]} />
                    <FilterSelect testid="filter-channel" label="Channel" value={filters.channel_id} onChange={(v) => setFilters((f) => ({ ...f, channel_id: v }))}
                        options={[{ value: "all", label: "all" }, ...channels.map((c) => ({ value: c.channel_id, label: c.channel_name }))]} />
                    <FilterSelect testid="filter-campaign" label="Campaign" value={filters.campaign_id} onChange={(v) => setFilters((f) => ({ ...f, campaign_id: v }))}
                        options={[{ value: "all", label: "all" }, ...campaigns.map((c) => ({ value: c.campaign_id, label: c.campaign_name }))]} />
                    <FilterSelect testid="filter-theme" label="Theme" value={filters.theme} onChange={(v) => setFilters((f) => ({ ...f, theme: v }))}
                        options={THEMES} />
                    <FilterSelect testid="filter-darkness" label="Darkness" value={filters.darkness_level} onChange={(v) => setFilters((f) => ({ ...f, darkness_level: v === "all" ? "all" : Number(v) }))}
                        options={["all", 1, 2, 3, 4, 5]} format={(v) => (v === "all" ? "all" : `D${v}`)} />
                    <FilterSelect testid="filter-tier" label="Tier" value={filters.content_tier} onChange={(v) => setFilters((f) => ({ ...f, content_tier: v }))}
                        options={TIERS} />
                </div>
                <label className="mt-4 inline-flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="filter-include-demo"
                        checked={filters.include_demo}
                        onChange={(e) => setFilters((f) => ({ ...f, include_demo: e.target.checked }))}
                        className="w-4 h-4 accent-[#ff2a2a]"
                    />
                    <span className="font-mono-d8 text-xs uppercase tracking-wider text-[#f5f5f0]">Include demo fallback</span>
                </label>
            </section>

            {isEmpty && (
                <section className="px-6 md:px-12 py-16">
                    <div
                        data-testid="empty-state-panel"
                        className="border border-dashed border-[#262626] p-12 text-center"
                    >
                        <div className="d8-label mb-3">No live analytics yet</div>
                        <p className="text-[#8a8a8a] text-sm max-w-md mx-auto">
                            Ingest a test event from <span className="text-[#f5f5f0] font-mono-d8">Ops → Ingest</span> or enable demo fallback above.
                        </p>
                    </div>
                </section>
            )}

            {!isEmpty && (
                <section className="p-6 md:p-12 space-y-8">
                    {showLive && data.live?.totals && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#262626] border border-[#262626]">
                            {[
                                ["Events (live)", data.live.totals.event_count],
                                ["Views", (data.live.totals.views || 0).toLocaleString()],
                                ["Completion avg", data.live.totals.completion_rate_avg !== null ? `${Math.round((data.live.totals.completion_rate_avg || 0) * 100)}%` : "—"],
                                ["Shares", (data.live.totals.shares || 0).toLocaleString()],
                                ["Reported", (data.live.totals.reported_count || 0).toLocaleString()],
                            ].map(([k, v]) => (
                                <div key={k} className="bg-[#0a0a0a] p-5">
                                    <div className="d8-label mb-1">{k}</div>
                                    <div className="font-mono-d8 text-2xl text-[#f5f5f0] tabular-nums">{v}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <Panel
                            title="8s retention curve"
                            testid="chart-retention"
                            note={showDemo && !showLive ? "DEMO" : showDemo ? "MIXED" : undefined}
                        >
                            <ResponsiveContainer width="100%" height={240}>
                                <AreaChart data={primary.retention_curve}>
                                    <CartesianGrid stroke="#262626" vertical={false} />
                                    <XAxis dataKey="second" stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} />
                                    <YAxis stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} domain={[0, 100]} />
                                    <Tooltip contentStyle={chartTooltipStyle} />
                                    <Area type="step" dataKey="pct" stroke="#ff2a2a" fill="#ff2a2a22" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Panel>

                        <Panel title="Darkness distribution" testid="chart-darkness" note={showDemo && !showLive ? "DEMO" : undefined}>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={primary.darkness_distribution || []}>
                                    <CartesianGrid stroke="#262626" vertical={false} />
                                    <XAxis dataKey="level" stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} tickFormatter={(v) => `D${v}`} />
                                    <YAxis stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} />
                                    <Tooltip contentStyle={chartTooltipStyle} />
                                    <Bar dataKey={primary.darkness_distribution?.[0]?.views !== undefined ? "views" : "clips"}>
                                        {(primary.darkness_distribution || []).map((d) => (
                                            <Cell key={d.level} fill={d.level >= 4 ? "#ff2a2a" : "#f5f5f0"} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Panel>

                        <Panel title="Theme performance" testid="chart-themes" note={showDemo && !showLive ? "DEMO" : undefined}>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={primary.theme_breakdown || []} layout="vertical" margin={{ left: 30 }}>
                                    <CartesianGrid stroke="#262626" horizontal={false} />
                                    <XAxis type="number" stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} />
                                    <YAxis dataKey="theme" type="category" stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={10} width={120} />
                                    <Tooltip contentStyle={chartTooltipStyle} />
                                    <Bar dataKey={primary.theme_breakdown?.[0]?.completion_rate_avg !== undefined ? "completion_rate_avg" : "too_real_freq"} fill="#ff2a2a" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Panel>

                        <Panel title="Platform breakdown" testid="chart-platforms" note={showDemo && !showLive ? "DEMO" : undefined}>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={primary.platform_breakdown || []}>
                                    <CartesianGrid stroke="#262626" vertical={false} />
                                    <XAxis dataKey="platform" stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} />
                                    <YAxis stroke="#8a8a8a" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={11} />
                                    <Tooltip contentStyle={chartTooltipStyle} />
                                    <Bar dataKey="shares" fill="#f5f5f0" />
                                    <Bar dataKey="saves" fill="#ff2a2a" />
                                    <Bar dataKey="comments" fill="#8a8a8a" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Panel>
                    </div>

                    <Panel title="Sentiment clusters" testid="chart-sentiment" note={showDemo && !showLive ? "DEMO" : undefined}>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[#262626]">
                            {(primary.sentiment_clusters || []).map((c) => (
                                <div key={c.label} className="bg-[#050505] p-5">
                                    <div className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#8a8a8a] mb-2">
                                        {c.label.replace(/_/g, " ")}
                                    </div>
                                    <div className="h-1.5 bg-[#0a0a0a] border border-[#262626]">
                                        <div className="h-full bg-[#ff2a2a]" style={{ width: `${c.weight * 100}%` }} />
                                    </div>
                                    <div className="mt-2 font-mono-d8 text-sm text-[#f5f5f0] tabular-nums">
                                        {(c.weight * 100).toFixed(1)}%
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>

                    <div className="d8-label">
                        Generated: {new Date(data.generated_at).toLocaleString()} · library {data.totals_extra?.clips_in_library ?? 0} · channels {data.totals_extra?.channels ?? 0} · campaigns {data.totals_extra?.campaigns ?? 0}
                    </div>

                    <div className="space-y-3">
                        <div className="d8-label">/ Routing summary</div>
                        <RoutingPanel />
                    </div>
                </section>
            )}
        </div>
    );
};

const FilterSelect = ({ label, value, onChange, options, testid, format }) => (
    <div>
        <div className="d8-label mb-1">{label}</div>
        <select
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-wider p-2.5 focus:outline-none focus:border-[#ff2a2a]"
        >
            {options.map((o) => {
                if (typeof o === "object") return <option key={o.value} value={o.value}>{o.label}</option>;
                return <option key={o} value={o}>{format ? format(o) : String(o).replace(/_/g, " ")}</option>;
            })}
        </select>
    </div>
);

export default Dashboard;
